import React, { useState } from "react";
import { Row, Modal, Form } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";

import { FormContext } from "../../../../reusable_components/input_fields/FormContext";
import Element, { FieldProps } from "../../../../reusable_components/input_fields/Input";
import TemplateDownload from "../../../../../Reusables/components/TemplateDownload/TemplateDownload";
import alertModalStyles from "../../../../../Reusables/components/AlertModal/AlertModal.module.css";
import CustomButton from "../../../../reusable_components/custom_button_component/Button";
import { CustomToaster } from "../../../../reusable_components/Toasters/toaster";

import { handleChange } from "./COIRegistryUploadModal.utils";
import { uploadCOIRegistryBulk } from "./COIRegistryUploadModal.service";
import { COIRegistryUploadModalProps, COIRegistryBulkUploadResult } from "./COIRegistryUploadModal.types";
import styles from "./COIRegistryUploadModal.module.css";

const COIRegistryUploadModal: React.FC<COIRegistryUploadModalProps> = ({ show, onHide }) => {
  const [fileUploading, setFileUploading] = useState(false);
  const [result, setResult] = useState<COIRegistryBulkUploadResult>();

  const [elements, setElements] = useState<FieldProps[]>([
    {
      id: uuidv4(),
      label: null,
      mandatory: false,
      approval: false,
      disabled: false,
      placeholder: "Upload Spreadsheet File",
      type: "file",
      example: null,
      pivot: true,
      pivotreverse: false,
      value: "",
      width: 12,
      fileTypes: ".xlsx,.csv,.xls",
    },
  ]);

  const handleSubmit = async () => {
    if (!elements[0].value) return;

    setFileUploading(true);
    setResult(undefined);
    try {
      const uploadResult = await uploadCOIRegistryBulk(elements[0].value);
      setResult(uploadResult ?? undefined);

      if (uploadResult && uploadResult.SuccessCount > 0) {
        CustomToaster({
          type: "success",
          customized_message: `${uploadResult.SuccessCount} of ${uploadResult.TotalRows} row(s) saved successfully.`,
          api_message: "",
        });
      }

      if (uploadResult && uploadResult.FailureCount === 0) {
        onHide(true);
      }
    } catch (err: any) {
      CustomToaster({
        type: "error",
        customized_message: err?.message || "Something went wrong while uploading.",
        api_message: "",
      });
    } finally {
      setFileUploading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => onHide(!!result && result.SuccessCount > 0)}
      size="lg"
      centered
      keyboard={false}
      dialogClassName={alertModalStyles.dialog}
      contentClassName={alertModalStyles.content}
      backdropClassName={alertModalStyles.backdrop}
    >
      <Modal.Header closeButton className={alertModalStyles.header}>
        <Modal.Title className={alertModalStyles.title}>Bulk Upload COI Registry</Modal.Title>
      </Modal.Header>

      <Modal.Body className={styles.body}>
        <div className={styles.templateDownload}>
          <TemplateDownload
            title={"Spreadsheet Template (.xlsx)"}
            endpoint="/firm_setup/coi_registry_template_excel"
            params={{}}
            extension={"xlsx"}
            filename={"COI_Registry_Bulk_Upload"}
          />
        </div>

        <FormContext.Provider
          value={{
            handleChange: (field_id, event) => handleChange(elements, setElements, field_id, event),
          }}
        >
          <Form>
            <Row>
              <label className={`${alertModalStyles.message} ${styles.spreadsheetLabel}`}>
                Upload Spreadsheet File
              </label>

              {elements.map((field, i) => (
                <Element key={i} field={field} md={12} />
              ))}

              {elements[0].value && (
                <div className={styles.filePreviewContainer}>
                  <div className={styles.filePreview}>
                    <div className={styles.fileLeft}>
                      <span>📄</span>
                      <span className={styles.fileName}>{elements[0].value.name}</span>
                    </div>
                    <span className={styles.removeIcon} onClick={() => setElements((prev) => [{ ...prev[0], value: "" }])}>
                      ✖
                    </span>
                  </div>
                </div>
              )}
            </Row>
          </Form>
        </FormContext.Provider>

        {result && (
          <div className={styles.resultSummary}>
            <strong>
              {result.SuccessCount} of {result.TotalRows} row(s) saved.
            </strong>
            {result.Errors.length > 0 && (
              <div className={styles.errorList}>
                {result.Errors.map((error, i) => (
                  <div key={i} className={styles.errorRow}>
                    Row {error.Row}: {error.Message}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className={alertModalStyles.footer}>
        <CustomButton onclick={() => onHide(!!result && result.SuccessCount > 0)} content="Close" type="quinary" />
        <CustomButton
          progress={fileUploading}
          onclick={handleSubmit}
          content="Submit"
          disabled={!elements[0].value}
          type="tertiary"
        />
      </Modal.Footer>
    </Modal>
  );
};

export default COIRegistryUploadModal;
